import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import Testimonials from '@src/pages/LandingPage/Testimonials'
import junkTestimonials from '@src/constants/junkTestimonials'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import recurring from '../../json/recurring-waste-services-data.json'

const recurringServiceData = {
  name: 'Recurring Waste Management Services',
  description:
    'Nationwide recurring waste collection for businesses and households on a weekly, biweekly, monthly, or quarterly schedule, with a chosen arrival window, 15% off every scheduled service, and a diversion report after each pickup.',
  serviceType: 'Recurring Waste Collection',
  url: 'https://getdimension.com/recurring-waste-services',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Weekly Waste Collection' },
    { name: 'Biweekly Recycling Collection' },
    { name: 'Monthly Waste and Large Item Pickup' },
    { name: 'Quarterly Waste Collection' },
    { name: 'Recurring Dumpster Swaps' },
  ],
}

const recurringFAQData = {
  questions: recurring.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function RecurringWasteServices() {
  return (
    <>
      <Head>
        <title>Recurring Waste Management Solutions | Dimension</title>
        <meta
          name="description"
          content="Recurring waste collection nationwide — weekly, biweekly, monthly, or quarterly. Choose your day and arrival hour, and save 15% on every scheduled service."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/recurring-waste-services" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={recurringServiceData} />
        <SchemaMarkup type="FAQPage" data={recurringFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="junk"
          heading={recurring.pitch.heading}
          material={recurring.pitch.material}
          pitchText={recurring.pitch.pitchText}
          ctaLink={recurring.pitch.ctaLink}
          ctaText={recurring.pitch.ctaText}
          ctaHeading={recurring.pitch.ctaHeading}
          ctaSubtext={recurring.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Testimonials testimonials={junkTestimonials} />
        <Clients />
        <FeatureList {...recurring.solutions} />
        <FeatureList {...recurring.benefits} />
        <CaseStudy {...recurring.caseStudy} />
        <Approach {...recurring.approach} />
        <FAQ {...recurring.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Commercial Waste Disposal',
              href: '/commercial-waste-disposal',
              description:
                'Scheduled and one-off collection for restaurants, retail, offices, and warehouses, with volume pricing.',
            },
            {
              label: 'Residential Trash and Recycling',
              href: '/residential-trash-and-recycling',
              description:
                'Household, apartment, and HOA pickups on a cadence, with an arrival hour you choose.',
            },
            {
              label: 'Dumpster Swaps',
              href: '/dumpster-swaps',
              description:
                'Full container out, empty container in — the recurring product for sites that fill a roll-off.',
            },
            {
              label: 'Cardboard Recycling',
              href: '/cardboard-recycling',
              description:
                'Dedicated cardboard collection, the stream most worth running on its own schedule.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default RecurringWasteServices
